import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { format, parseISO } from 'date-fns';
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBBtn,
  MDBIcon,
  MDBCard,
  MDBCardBody,
  MDBInput,
  MDBModal,
  MDBModalHeader,
  MDBModalBody,
  MDBModalFooter,
  MDBSpinner,
} from 'mdbreact';
import { toast } from 'react-toastify';
import NavBar from '../../../components/NavBar';
import api from '../../../services/api';
import { permissaoDeRota } from '../../../services/auth';
import logotipoDoc from '../../../assets/logotipo_doc_novo.png';
import {
  formataCPF,
  formataCNPJ,
  formataCNH,
  formataCpfCnpj,
  formataDocumento,
} from '../../../utils/mascaras';

// formata uma data DATEONLY (yyyy-MM-dd) como dd/MM/yyyy. Retorna '' se
// vazia/invalida (nao quebra se vier algum texto legado).
function fmtData(valor) {
  if (!valor) return '';
  try {
    return format(parseISO(String(valor)), 'dd/MM/yyyy');
  } catch (e) {
    return String(valor);
  }
}

// Exibe a Dinamica: pode conter HTML (texto + imagens base64) do editor novo,
// ou texto puro (RTs antigos). Faz uma sanitizacao leve antes de injetar como
// HTML (remove <script> e handlers on*, que o editor nunca gera mas evitam XSS
// caso o conteudo tenha sido adulterado).
function DinamicaView({ html }) {
  if (!html || !String(html).trim()) return <span>—</span>;
  const str = String(html);
  const temHtml = /<[a-z!/][\s\S]*>/i.test(str);

  if (!temHtml) {
    // texto puro: preserva quebras de linha
    return <span style={{ whiteSpace: 'pre-wrap' }}>{str}</span>;
  }

  const limpo = str
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/ on\w+="[^"]*"/gi, '')
    .replace(/ on\w+='[^']*'/gi, '');

  return (
    <span
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: limpo }}
      style={{ display: 'inline-block', maxWidth: '100%' }}
    />
  );
}

// Bloco read-only de PJ no modal de detalhes (5/6/7/8). So renderiza se
// houver algum campo preenchido.
function DetalhePJ({ titulo, razao, cnpj, dataAbertura, capital, endereco }) {
  if (!(razao || cnpj || dataAbertura || capital || endereco)) return null;
  return (
    <div className="mb-2">
      <p className="font-weight-bold mb-1">{titulo}</p>
      <p className="mb-0">Razão social: {razao || '—'}</p>
      <p className="mb-0">
        CNPJ: {cnpj ? formataCNPJ(cnpj) : '—'} &nbsp; Data abertura:{' '}
        {dataAbertura ? fmtData(dataAbertura) : '—'} &nbsp; Capital social:{' '}
        {capital || '—'}
      </p>
      <p className="mb-0">Endereço: {endereco || '—'}</p>
    </div>
  );
}

// Lista read-only de socios de uma secao no modal (4.1/5.1/6.1/7.1/8.1).
function DetalheSocios({ titulo, socios }) {
  if (!socios || socios.length === 0) return null;
  return (
    <div className="mb-2">
      <p className="font-weight-bold mb-1">{titulo}</p>
      {socios.map((s, i) => (
        <div key={s.id} className="mb-1">
          <p className="mb-0 font-weight-bold">
            Sócio {i + 1}
            {s.tipo === 'PJ' ? ' (Sócio PJ)' : ''}
          </p>
          <p className="mb-0">
            {s.tipo === 'PJ' ? 'CNPJ' : 'CPF'}:{' '}
            {s.cpf ? formataDocumento(s.cpf, s.tipo) : '—'} &nbsp; Nome:{' '}
            {s.nome || '—'}
          </p>
          <p className="mb-0">Endereço: {s.endereco || '—'}</p>
          <p className="mb-0">Registro Criminal: {s.registro_criminal || '—'}</p>
        </div>
      ))}
    </div>
  );
}

// Listagem dos Relatorios Tecnicos (RT) - modulo Inteligencia.
export default function RelatoriosTecnicos(props) {
  // tipo exibido por esta rota (INTELIGENCIA padrao; CONTRA_INTELIGENCIA
  // quando renderizada por /ci-relatorios-tecnicos)
  const tipo = props.tipo || 'INTELIGENCIA';
  const ehRelintCi = tipo === 'RELINT_CI';
  // RELINT_CI compartilha o layout do RELINT (cabecalho, campo Avaliacao,
  // renumeracao, nome de arquivo); difere so na rota de criacao e no titulo.
  const ehRelint = tipo === 'RELINT' || ehRelintCi;
  const ehPedidoBuscaCi = tipo === 'PEDIDO_BUSCA_CI';
  const ehPedidoBusca = tipo === 'PEDIDO_BUSCA' || ehPedidoBuscaCi;
  const ehOrdemBuscaCi = tipo === 'ORDEM_BUSCA_CI';
  const ehOrdemBusca = tipo === 'ORDEM_BUSCA' || ehOrdemBuscaCi;
  // Pedido de Busca e Ordem de Busca compartilham o layout (sem Dinamica, com
  // os 3 campos de texto). Diferem so em nomenclatura/numeracao/rota/ACL.
  const ehSemDinamica = ehPedidoBusca || ehOrdemBusca;

  // rota de criacao deste tipo; o botao "Novo Relatório Técnico" so habilita
  // se o usuario tem essa rota nos acessos do token (mesma regra do
  // PrivateRoute) — o item de menu de criacao foi removido (card=false).
  // rota de criacao por tipo (map em vez de ternario aninhado: uma cadeia de
  // ?: longa demais confunde o eslint-plugin-react-hooks e quebra o build).
  const ROTAS_CRIAR = {
    PEDIDO_BUSCA_CI: '/ci-pedido-busca-criar',
    PEDIDO_BUSCA: '/pedido-busca-criar',
    ORDEM_BUSCA_CI: '/ci-ordem-busca-criar',
    ORDEM_BUSCA: '/ordem-busca-criar',
    RELATORIO_AGENTE_CI: '/ci-relatorio-agente-criar',
    RELATORIO_AGENTE: '/relatorio-agente-criar',
    RELINT_CI: '/ci-relint-criar',
    RELINT: '/relint-criar',
    CONTRA_INTELIGENCIA: '/ci-relatorio-tecnico-criar',
  };
  // ACL de escopo (RELINT da propria gerencia): as telas sao as mesmas, mas as
  // rotas de criacao/retorno sao as restritas (o usuario nao tem /relint-criar).
  const restritoGerencia = props.restritoGerencia === true;
  const rotaCriar = restritoGerencia
    ? '/relint-gerencia-criar'
    : ROTAS_CRIAR[tipo] || '/relatorio-tecnico-criar';

  // prefixo do cabecalho impresso e do carimbo dos anexos, por tipo.
  const PREFIXOS_DOC = {
    PEDIDO_BUSCA: 'PB SESEG/SSINTE/',
    PEDIDO_BUSCA_CI: 'PB SESEG/SSINTE/',
    ORDEM_BUSCA: 'OB SESEG/SSINTE/',
    ORDEM_BUSCA_CI: 'OB SESEG/SSINTE/',
    RELATORIO_AGENTE: 'RA SESEG/SSINTE/',
    RELATORIO_AGENTE_CI: 'RA SESEG/SSINTE/',
    RELINT: 'RELINT SESEG/SSINTE/',
    RELINT_CI: 'RELINT SESEG/SSINTE/',
  };
  const prefixoBaseDoc = PREFIXOS_DOC[tipo] || 'RT SESEG/SSINTE/';
  // cabecalho completo p/ um documento: usa a sigla GRAVADA no doc (snapshot)
  const prefixoDoDocumento = rt => `${prefixoBaseDoc}${rt.sigla_agencia || 'S181'}`;

  // prefixo do nome do arquivo PDF, por tipo.
  const PREFIXOS_ARQ = {
    PEDIDO_BUSCA: 'PB',
    PEDIDO_BUSCA_CI: 'PB',
    ORDEM_BUSCA: 'OB',
    ORDEM_BUSCA_CI: 'OB',
    RELATORIO_AGENTE: 'RA',
    RELATORIO_AGENTE_CI: 'RA',
    RELINT: 'RELINT',
    RELINT_CI: 'RELINT',
  };
  const prefixoArqTipo = PREFIXOS_ARQ[tipo] || 'RT';

  // titulo da tela, por tipo.
  const TITULOS = {
    PEDIDO_BUSCA: 'Pedido de Busca',
    PEDIDO_BUSCA_CI: 'Pedido de Busca — Contra Inteligência',
    ORDEM_BUSCA: 'Ordem de Busca',
    ORDEM_BUSCA_CI: 'Ordem de Busca — Contra Inteligência',
    RELATORIO_AGENTE: 'Relatório de Agente',
    RELATORIO_AGENTE_CI: 'Relatório de Agente — Contra Inteligência',
    RELINT: 'RELINT',
    RELINT_CI: 'RELINT — Contra Inteligência',
    CONTRA_INTELIGENCIA: 'Relatórios Técnicos — Contra Inteligência',
  };
  // tituloTela = NOME DO DOCUMENTO. E usado tambem no cabecalho das exportacoes
  // (coluna "Tipo") e como titulo do PDF, entao NAO pode carregar o sufixo de
  // escopo — senao os arquivos exportados sairiam com "— Minha Gerência".
  const tituloTela = TITULOS[tipo] || 'Relatórios Técnicos';
  // rotulo mostrado no topo da tela (so visual): deixa claro o escopo restrito.
  const tituloExibicao =
    tituloTela + (restritoGerencia ? ' — Minha Gerência' : '');

  // texto do botao "Novo ...", por tipo.
  const NOMES_NOVO = {
    PEDIDO_BUSCA: 'Novo Pedido de Busca',
    PEDIDO_BUSCA_CI: 'Novo Pedido de Busca',
    ORDEM_BUSCA: 'Nova Ordem de Busca',
    ORDEM_BUSCA_CI: 'Nova Ordem de Busca',
    RELATORIO_AGENTE: 'Novo Relatório de Agente',
    RELATORIO_AGENTE_CI: 'Novo Relatório de Agente',
    RELINT: 'Novo RELINT',
    RELINT_CI: 'Novo RELINT',
  };
  const textoNovo = NOMES_NOVO[tipo] || 'Novo Relatório Técnico';

  // nome SINGULAR do documento, p/ mensagens/toasts/titulos ("Nenhum X
  // encontrado", "Excluir X", ...). Mesmo padrao de map por tipo.
  const NOMES_DOC = {
    PEDIDO_BUSCA: 'Pedido de Busca',
    PEDIDO_BUSCA_CI: 'Pedido de Busca',
    ORDEM_BUSCA: 'Ordem de Busca',
    ORDEM_BUSCA_CI: 'Ordem de Busca',
    RELATORIO_AGENTE: 'Relatório de Agente',
    RELATORIO_AGENTE_CI: 'Relatório de Agente',
    RELINT: 'RELINT',
    RELINT_CI: 'RELINT',
  };
  const nomeDoc = NOMES_DOC[tipo] || 'Relatório Técnico';
  // artigo/flexao: "Nenhum(a) X encontrado(a)" — Ordem de Busca e feminino
  const docFeminino = tipo === 'ORDEM_BUSCA' || tipo === 'ORDEM_BUSCA_CI';
  const podeCriar = permissaoDeRota(rotaCriar);

  const [relatorios, setRelatorios] = useState([]);
  const [page, setPage] = useState(1);
  const [qtdTotal, setQtdTotal] = useState(1);
  const [loading, setLoading] = useState(false);

  const [dataInicio, setDataInicio] = useState('');
  const [dataFinal, setDataFinal] = useState('');
  // busca livre em qualquer parte do documento
  const [busca, setBusca] = useState('');

  // modal de detalhes / exclusao
  const [detalhe, setDetalhe] = useState(null);
  const [idExcluir, setIdExcluir] = useState(null);
  const [excluindo, setExcluindo] = useState(false);

  // preview do documento (layout impresso) no "Ver": guarda o RT em exibicao,
  // a URL do blob do PDF e o estado de carregamento.
  const [rtPreview, setRtPreview] = useState(null);
  const [pdfUrl, setPdfUrl] = useState('');
  const [gerandoPdf, setGerandoPdf] = useState(false);
  const [exportando, setExportando] = useState(false);

  const buscar = async () => {
    setLoading(true);
    try {
      const params = { page };
      params.tipo = tipo;
      if (dataInicio) params.data_inicio = dataInicio;
      if (dataFinal) params.data_final = dataFinal;
      if (busca && busca.trim() !== '') params.busca = busca.trim();

      const result = await api.get('/relatorio-tecnico', { params });
      setRelatorios(result.data.rows || []);
      setQtdTotal(Math.max(1, Math.ceil(Number(result.data.count) / 10)));
    } catch (e) {
      toast.error(`Falha ao carregar — ${tituloTela}`);
    }
    setLoading(false);
  };

  // busca TODOS os documentos do filtro atual (sem paginacao) p/ exportar.
  const buscarTodosParaExport = async () => {
    const params = { tipo, limit: 'all' };
    if (dataInicio) params.data_inicio = dataInicio;
    if (dataFinal) params.data_final = dataFinal;
    if (busca && busca.trim() !== '') params.busca = busca.trim();
    const result = await api.get('/relatorio-tecnico', { params });
    return result.data.rows || [];
  };

  // nome base do arquivo de export por tipo (usa o prefixo ja derivado).
  const nomeArqExport = sufixo =>
    `${prefixoArqTipo}_documentos${sufixo || ''}`;

  // uma linha por documento com os campos principais (chaves = cabecalho).
  const dataBR = d => (d ? format(parseISO(d), 'dd/MM/yyyy') : '');
  const montaLinhasPrincipais = lista =>
    lista.map(rt => ({
      'Número': formataNumero(rt),
      Ano: rt.ano || '',
      Data: dataBR(rt.data),
      Assunto: rt.assunto || '',
      Origem: rt.origem || '',
      'Difusão': rt.difusao || '',
      'Difusão Anterior': rt.difusao_anterior || '',
      'Referência': rt.referencia || '',
      'Precedência': rt.precedencia || '',
      Tipo: tituloTela,
      Autor: nomeAutor(rt),
    }));

  // linhas p/ o i2: campos principais + entidades (uma linha por documento).
  const montaLinhasI2 = lista =>
    lista.map(rt => ({
      'Número': formataNumero(rt),
      Ano: rt.ano || '',
      Data: dataBR(rt.data),
      Assunto: rt.assunto || '',
      Origem: rt.origem || '',
      Tipo: tituloTela,
      Autor: nomeAutor(rt),
      'Motorista Nome': rt.motorista_nome || '',
      'Motorista CPF': rt.motorista_cpf || '',
      'Motorista CNH': rt.motorista_cnh || '',
      'Veículo Placa': rt.veiculo_placa || '',
      'Veículo Chassi': rt.veiculo_chassi || '',
      'Veículo Marca/Modelo': rt.veiculo_marca_modelo || '',
      'Veículo Proprietário': rt.veiculo_proprietario || '',
      'Veículo CPF/CNPJ': rt.veiculo_cpf_cnpj || '',
      'Semirreboque Placa': rt.semirreboque_placa || '',
      'Semirreboque Chassi': rt.semirreboque_chassi || '',
      'Semirreboque Proprietário': rt.semirreboque_proprietario || '',
      'Semirreboque CPF/CNPJ': rt.semirreboque_cpf_cnpj || '',
      'PJ CNPJ': rt.pj_cnpj || '',
      'PJ Razão Social': rt.pj_razao_social || '',
      'Emitente CNPJ': rt.emitente_cnpj || '',
      'Emitente Razão Social': rt.emitente_razao_social || '',
      'Destinatário CNPJ': rt.dest_cnpj || '',
      'Destinatário Razão Social': rt.dest_razao_social || '',
      'Transportadora CNPJ': rt.transp_cnpj || '',
      'Transportadora Razão Social': rt.transp_razao_social || '',
      'Manifesto CNPJ': rt.manifesto_cnpj || '',
      'Manifesto Razão Social': rt.manifesto_razao_social || '',
    }));

  // serializa um array de objetos como CSV (; , aspas, BOM p/ Excel).
  const linhasParaCsv = linhas => {
    const colunas = Object.keys(linhas[0]);
    const escapa = v => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const conteudo = [
      colunas.map(escapa).join(';'),
      ...linhas.map(l => colunas.map(c => escapa(l[c])).join(';')),
    ].join('\r\n');
    return new Blob([`﻿${conteudo}`], {
      type: 'text/csv;charset=utf-8;',
    });
  };

  // roda a busca completa, aborta com toast se vazio, e chama o gerador.
  const comDadosExport = async gerar => {
    if (exportando) return;
    setExportando(true);
    try {
      const lista = await buscarTodosParaExport();
      if (!lista.length) {
        toast.error('Nada para exportar. Ajuste o filtro e tente de novo.');
        return;
      }
      gerar(lista);
    } catch (e) {
      toast.error('Falha ao exportar os documentos.');
    } finally {
      setExportando(false);
    }
  };

  const exportarCSV = () =>
    comDadosExport(lista =>
      saveAs(linhasParaCsv(montaLinhasPrincipais(lista)), `${nomeArqExport()}.csv`)
    );

  const exportarXLSX = () =>
    comDadosExport(lista => {
      const ws = XLSX.utils.json_to_sheet(montaLinhasPrincipais(lista));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Documentos');
      XLSX.writeFile(wb, `${nomeArqExport()}.xlsx`);
    });

  const exportarPDF = () =>
    comDadosExport(lista => {
      const linhas = montaLinhasPrincipais(lista);
      const colunas = Object.keys(linhas[0]);
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      doc.setFontSize(12);
      doc.text(tituloTela, 14, 14);
      doc.autoTable({
        startY: 20,
        head: [colunas],
        body: linhas.map(l => colunas.map(c => l[c])),
        styles: { fontSize: 7, cellPadding: 1.5, overflow: 'linebreak' },
        headStyles: { fillColor: [89, 105, 141] },
        margin: { left: 8, right: 8 },
      });
      doc.save(`${nomeArqExport()}.pdf`);
    });

  const exportarI2 = () =>
    comDadosExport(lista =>
      saveAs(linhasParaCsv(montaLinhasI2(lista)), `${nomeArqExport('_i2')}.csv`)
    );

  useEffect(() => {
    buscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const baixarAnexo = async (id, nome) => {
    try {
      const blob = await api.get('/rt-pdf-download', {
        params: { id },
        responseType: 'blob',
      });
      saveAs(blob.data, nome || 'anexo.pdf');
    } catch (e) {
      toast.error('Falha ao baixar o anexo');
    }
  };

  const excluir = async () => {
    setExcluindo(true);
    try {
      await api.delete(`/relatorio-tecnico/${idExcluir}`);
      setRelatorios(prev => prev.filter(r => r.id !== idExcluir));
      toast.success(`${nomeDoc} removido${docFeminino ? 'a' : ''}`);
    } catch (e) {
      toast.error('Falha ao remover');
    }
    setExcluindo(false);
    setIdExcluir(null);
  };

  const formataNumero = rt =>
    `${String(rt.numero).padStart(4, '0')}/${rt.ano}`;

  const nomeAutor = rt => {
    if (!rt.usuario) return '—';
    const completo = [rt.usuario.nome, rt.usuario.sobrenome]
      .filter(Boolean)
      .join(' ')
      .trim();
    return rt.usuario.nome_social || completo || '—';
  };

  // carrega a imagem (asset importado -> URL) e devolve um dataURL base64,
  // forma confiavel/sincrona para o jsPDF.addImage no navegador.
  const carregaLogoDataURL = src =>
    new Promise(resolve => {
      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            canvas.getContext('2d').drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/png'));
          } catch (e) {
            resolve(null);
          }
        };
        img.onerror = () => resolve(null);
        img.src = src;
      } catch (e) {
        resolve(null);
      }
    });

  // Gera o PDF do RT no layout oficial (Anexo C - Modelo de RT) e RETORNA o
  // Blob final (documento + anexos mesclados). Nao baixa: quem chama decide
  // (baixar via saveAs no "Imprimir", ou exibir num iframe no "Ver").
  // opts.baixarNaoMesclaveis: baixa docx/xlsx separado (so no fluxo Imprimir).
  const gerarPdfRT = async (rt, opts = {}) => {
    const { baixarNaoMesclaveis = true } = opts;
    const logoDataURL = await carregaLogoDataURL(logotipoDoc);

    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const larguraPag = doc.internal.pageSize.getWidth(); // 210
    const margemEsq = 20;
    const margemDir = larguraPag - 20;
    const centro = larguraPag / 2;

    // limites verticais do conteudo dentro da moldura
    const molduraTopo = 26;
    const molduraBase = 264; // 26 + 238
    const conteudoBase = 258; // limite p/ o texto nao encostar na borda inferior

    const aviso =
      '"O sigilo deste documento é protegido e controlado pela Lei Nº 12.527/2011. A divulgação, a revelação, o fornecimento, a utilização ou a ' +
      'reprodução desautorizada de seu conteúdo, a qualquer tempo, meio e modo, inclusive mediante acesso ou facilitação de acesso indevidos, ' +
      'constituem condutas ilícitas que ensejam responsabilidades penais, civis e administrativas."';

    // desenha o "chrome" fixo de UMA pagina: RESERVADO, moldura e rodape
    // (aviso da Lei + RESERVADO). Usado em todas as paginas.
    const desenhaMolduraPagina = numPag => {
      // topo: RESERVADO + numero da pagina
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(200, 0, 0);
      doc.text('RESERVADO', centro, 14, { align: 'center' });
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(8);
      doc.text(String(numPag), margemDir, 14, { align: 'right' });

      // moldura
      doc.setDrawColor(0, 0, 0);
      doc.rect(margemEsq, molduraTopo, margemDir - margemEsq, 238);

      // rodape
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(0, 100, 0);
      const avisoLinhas = doc.splitTextToSize(aviso, margemDir - margemEsq);
      doc.text(avisoLinhas, margemEsq, 270);
      doc.setTextColor(0, 0, 0);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(200, 0, 0);
      doc.text('RESERVADO', centro, 288, { align: 'center' });
      doc.setTextColor(0, 0, 0);
    };

    let paginaAtual = 1;
    desenhaMolduraPagina(paginaAtual);

    // quebra para uma nova pagina do documento (mesma moldura) e retorna o
    // novo Y de topo do conteudo.
    const novaPaginaDocumento = () => {
      doc.addPage();
      paginaAtual += 1;
      desenhaMolduraPagina(paginaAtual);
      return molduraTopo + 8; // topo do conteudo na nova pagina
    };

    // ---- BRASAO (logo do documento) - so na 1a pagina ----
    if (logoDataURL) {
      try {
        const ladoLogo = 18; // mm
        doc.addImage(logoDataURL, 'PNG', centro - ladoLogo / 2, 29, ladoLogo, ladoLogo);
      } catch (e) {
        // se a imagem falhar, segue so com o cabecalho textual
      }
    }

    // ---- CABECALHO TEXTUAL (orgao) - so na 1a pagina ----
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('Governo do Estado do Rio de Janeiro', centro, 52, { align: 'center' });
    doc.text('Secretaria de Estado de Segurança', centro, 56, { align: 'center' });
    doc.text('Subsecretaria de Inteligência', centro, 60, { align: 'center' });

    // PRECEDENCIA (caixa vermelha a esquerda) - mostra o valor salvo
    // (ex.: URGENTE); se vazio, mostra o rotulo "PRECEDÊNCIA".
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(200, 0, 0);
    doc.setDrawColor(200, 0, 0);
    const txtPrec = rt.precedencia
      ? String(rt.precedencia).toUpperCase()
      : 'PRECEDÊNCIA';
    const wPrec = doc.getTextWidth(txtPrec) + 4;
    doc.rect(margemEsq + 4, 66, wPrec, 6.5);
    doc.text(txtPrec, margemEsq + 6, 70.5);
    doc.setTextColor(0, 0, 0);

    // ---- NUMERO DO DOCUMENTO ----
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const numeroDoc = `${prefixoDoDocumento(rt)} nº ${String(rt.numero).padStart(
      4,
      '0'
    )}/${rt.ano}`;
    doc.text(numeroDoc, margemEsq + 4, 82);

    // ---- OS 7 CAMPOS (RELINT: 8, com Avaliação) ----
    const campos = ehRelint
      ? [
          ['1. Data:', rt.data ? format(parseISO(rt.data), 'dd/MM/yyyy') : ''],
          ['2. Assunto:', rt.assunto || ''],
          ['3. Origem:', rt.origem || ''],
          ['4. Avaliação:', rt.avaliacao || ''],
          ['5. Difusão:', rt.difusao || ''],
          ['6. Difusão Anterior:', rt.difusao_anterior || ''],
          ['7. Referência:', rt.referencia || ''],
          [
            '8. Anexo:',
            (rt.relatorio_tecnico_pdfs || [])
              .map(a => a.descricao || a.nome)
              .join('; '),
          ],
        ]
      : [
          ['1. Data:', rt.data ? format(parseISO(rt.data), 'dd/MM/yyyy') : ''],
          ['2. Assunto:', rt.assunto || ''],
          ['3. Origem:', rt.origem || ''],
          ['4. Difusão:', rt.difusao || ''],
          ['5. Difusão Anterior:', rt.difusao_anterior || ''],
          ['6. Referência:', rt.referencia || ''],
          [
            '7. Anexo:',
            (rt.relatorio_tecnico_pdfs || [])
              .map(a => a.descricao || a.nome)
              .join('; '),
          ],
        ];

    let y = 92;
    const larguraConteudo = margemDir - (margemEsq + 4) - 4;
    campos.forEach(([rotulo, valor]) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      const linhas = doc.splitTextToSize(
        String(valor),
        larguraConteudo - doc.getTextWidth(rotulo)
      );
      const alturaCampo = Math.max(7, linhas.length * 5 + 2);
      // se o campo nao couber, quebra pagina
      if (y + alturaCampo > conteudoBase) {
        y = novaPaginaDocumento();
      }
      doc.setFont('helvetica', 'bold');
      doc.text(rotulo, margemEsq + 4, y);
      const xValor = margemEsq + 4 + doc.getTextWidth(rotulo) + 2;
      doc.setFont('helvetica', 'normal');
      doc.text(linhas, xValor, y);
      y += alturaCampo;
    });

    // linha horizontal separando os campos do corpo
    if (y + 4 > conteudoBase) {
      y = novaPaginaDocumento();
    }
    doc.setDrawColor(0, 0, 0);
    doc.line(margemEsq + 4, y, margemDir - 4, y);
    y += 8;

    // ---- PEDIDO DE BUSCA: 3 campos de texto no lugar da Dinamica ----
    if (ehSemDinamica) {
      const larguraPB = margemDir - (margemEsq + 4) - 4;
      const blocosPB = [
        ['1. Dados Conhecidos:', rt.dados_conhecidos],
        ['2. Dados Solicitados:', rt.dados_solicitados],
        ['3. Instruções Especiais:', rt.instrucoes_especiais],
      ];
      blocosPB.forEach(([rotulo, valor]) => {
        if (y + 7 > conteudoBase) y = novaPaginaDocumento();
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text(rotulo, margemEsq + 4, y);
        y += 6;
        doc.setFont('helvetica', 'normal');
        const linhas = doc.splitTextToSize(String(valor || ''), larguraPB);
        linhas.forEach(linha => {
          if (y + 5 > conteudoBase) y = novaPaginaDocumento();
          doc.text(linha, margemEsq + 8, y);
          y += 5;
        });
        y += 4;
      });
    }

    // ---- PCF (Posto de Controle Fiscal): acima da Dinamica ----
    if (rt.pcf && String(rt.pcf).trim() !== '') {
      if (y + 7 > conteudoBase) {
        y = novaPaginaDocumento();
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      const rotuloPcf = 'PCF (Posto de Controle Fiscal): ';
      doc.text(rotuloPcf, margemEsq + 4, y);
      // mede a largura do rotulo AINDA em bold (senao o valor sobrepoe o label)
      const larguraRotuloPcf = doc.getTextWidth(rotuloPcf);
      doc.setFont('helvetica', 'normal');
      doc.text(String(rt.pcf), margemEsq + 4 + larguraRotuloPcf, y);
      y += 8;
    }

    // ---- BLOCO MOTORISTA: abaixo do PCF, se algum campo preenchido ----
    const temMotorista =
      rt.motorista_nome ||
      rt.motorista_cpf ||
      rt.motorista_cnh ||
      rt.motorista_cat ||
      rt.motorista_validade ||
      rt.motorista_endereco ||
      rt.motorista_registro_criminal;

    if (temMotorista) {
      // escreve uma linha "rotulo: valor" respeitando a paginacao
      const linhaMotorista = (rotulo, valor) => {
        if (!valor || String(valor).trim() === '') return;
        if (y + 6 > conteudoBase) {
          y = novaPaginaDocumento();
        }
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text(rotulo, margemEsq + 4, y);
        // mede a largura do rotulo AINDA em bold (senao o valor sobrepoe)
        const xVal = margemEsq + 4 + doc.getTextWidth(rotulo) + 1;
        doc.setFont('helvetica', 'normal');
        const linhas = doc.splitTextToSize(
          String(valor),
          margemDir - 4 - xVal
        );
        doc.text(linhas, xVal, y);
        y += Math.max(6, linhas.length * 5 + 1);
      };

      // titulo do bloco
      if (y + 7 > conteudoBase) {
        y = novaPaginaDocumento();
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text('MOTORISTA', margemEsq + 4, y);
      y += 6;

      linhaMotorista('Nome: ', rt.motorista_nome);
      linhaMotorista('CPF: ', rt.motorista_cpf && formataCPF(rt.motorista_cpf));

      // CNH / Cat / Validade na mesma linha
      const partesCnh = [];
      if (rt.motorista_cnh)
        partesCnh.push(`CNH: ${formataCNH(rt.motorista_cnh)}`);
      if (rt.motorista_cat) partesCnh.push(`Cat.: ${rt.motorista_cat}`);
      if (rt.motorista_validade)
        partesCnh.push(`Validade: ${fmtData(rt.motorista_validade)}`);
      if (partesCnh.length > 0) {
        if (y + 6 > conteudoBase) {
          y = novaPaginaDocumento();
        }
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text(partesCnh.join('    '), margemEsq + 4, y);
        y += 6;
      }

      linhaMotorista('Endereço: ', rt.motorista_endereco);
      linhaMotorista('Registro Criminal: ', rt.motorista_registro_criminal);
      y += 2;
    }

    // recuo horizontal (mm) aplicado aos helpers abaixo. Usado p/ indentar os
    // subitens .1 (SOCIOS) na impressao, como acontece na tela.
    let recuo = 0;

    // helper generico "rotulo: valor" respeitando paginacao (reutilizado pelos
    // blocos de veiculo). identico ao linhaMotorista mas fora do escopo dele.
    const linhaCampo = (rotulo, valor) => {
      if (!valor || String(valor).trim() === '') return;
      if (y + 6 > conteudoBase) {
        y = novaPaginaDocumento();
      }
      const xIni = margemEsq + 4 + recuo;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(rotulo, xIni, y);
      // mede a largura do rotulo AINDA em bold (senao o valor sobrepoe o label)
      const xVal = xIni + doc.getTextWidth(rotulo) + 1;
      doc.setFont('helvetica', 'normal');
      const linhas = doc.splitTextToSize(String(valor), margemDir - 4 - xVal);
      doc.text(linhas, xVal, y);
      y += Math.max(6, linhas.length * 5 + 1);
    };

    // escreve uma linha ja montada (varios pares na mesma linha), com paginacao
    const linhaTexto = valor => {
      if (!valor || String(valor).trim() === '') return;
      if (y + 6 > conteudoBase) {
        y = novaPaginaDocumento();
      }
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(valor, margemEsq + 4 + recuo, y);
      y += 6;
    };

    // titulo de bloco (negrito), com paginacao
    const tituloBloco = titulo => {
      if (y + 7 > conteudoBase) {
        y = novaPaginaDocumento();
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(titulo, margemEsq + 4 + recuo, y);
      y += 6;
    };

    // ---- BLOCO 2 - VEICULO (CAVALO MECANICO): abaixo do motorista ----
    const temVeiculo =
      rt.veiculo_marca_modelo ||
      rt.veiculo_tipo ||
      rt.veiculo_placa ||
      rt.veiculo_cor ||
      rt.veiculo_ano ||
      rt.veiculo_chassi ||
      rt.veiculo_proprietario ||
      rt.veiculo_cpf_cnpj;

    if (temVeiculo) {
      tituloBloco('2. VEÍCULO (CAVALO MECÂNICO)');
      linhaCampo('Marca/Modelo: ', rt.veiculo_marca_modelo);

      // Tipo / Placa / Cor na mesma linha
      const partesTpc = [];
      if (rt.veiculo_tipo) partesTpc.push(`Tipo: ${rt.veiculo_tipo}`);
      if (rt.veiculo_placa) partesTpc.push(`Placa: ${rt.veiculo_placa}`);
      if (rt.veiculo_cor) partesTpc.push(`Cor: ${rt.veiculo_cor}`);
      linhaTexto(partesTpc.join('    '));

      // Ano / Chassi na mesma linha
      const partesAc = [];
      if (rt.veiculo_ano) partesAc.push(`Ano: ${rt.veiculo_ano}`);
      if (rt.veiculo_chassi) partesAc.push(`Chassi: ${rt.veiculo_chassi}`);
      linhaTexto(partesAc.join('    '));

      linhaCampo('Proprietário: ', rt.veiculo_proprietario);
      linhaCampo(
        'CPF/CNPJ: ',
        rt.veiculo_cpf_cnpj && formataCpfCnpj(rt.veiculo_cpf_cnpj)
      );
      y += 2;
    }

    // ---- BLOCO 3 - VEICULO (SEMIRREBOQUE): so se houver ----
    const temSemi =
      rt.semirreboque_placa ||
      rt.semirreboque_chassi ||
      rt.semirreboque_proprietario ||
      rt.semirreboque_cpf_cnpj;

    if (temSemi) {
      tituloBloco('3. VEÍCULO (SEMIRREBOQUE)');

      // Placa / Chassi na mesma linha
      const partesPc = [];
      if (rt.semirreboque_placa)
        partesPc.push(`Placa: ${rt.semirreboque_placa}`);
      if (rt.semirreboque_chassi)
        partesPc.push(`Chassi: ${rt.semirreboque_chassi}`);
      linhaTexto(partesPc.join('    '));

      linhaCampo('Proprietário: ', rt.semirreboque_proprietario);
      linhaCampo(
        'CPF/CNPJ: ',
        rt.semirreboque_cpf_cnpj && formataCpfCnpj(rt.semirreboque_cpf_cnpj)
      );
      y += 2;
    }

    // ---- BLOCO 4 - PJ REMETENTE DA CARGA ----
    const temPj =
      rt.pj_razao_social ||
      rt.pj_cnpj ||
      rt.pj_data_abertura ||
      rt.pj_capital_social ||
      rt.pj_endereco ||
      rt.coincide_nfe;

    if (temPj) {
      tituloBloco('4. PJ REMETENTE DA CARGA');
      linhaCampo('Razão social: ', rt.pj_razao_social);

      // CNPJ / Data abertura / Capital social na mesma linha
      const partesPj = [];
      if (rt.pj_cnpj) partesPj.push(`CNPJ: ${formataCNPJ(rt.pj_cnpj)}`);
      if (rt.pj_data_abertura)
        partesPj.push(`Data abertura: ${fmtData(rt.pj_data_abertura)}`);
      if (rt.pj_capital_social)
        partesPj.push(`Capital social: ${rt.pj_capital_social}`);
      linhaTexto(partesPj.join('    '));

      linhaCampo('Endereço: ', rt.pj_endereco);
      if (rt.coincide_nfe) {
        const sim = rt.coincide_nfe === 'SIM';
        linhaTexto(
          `Coincide com a NF-e?  SIM ( ${sim ? 'X' : ' '} )   NÃO ( ${
            sim ? ' ' : 'X'
          } )`
        );
      }
      y += 2;
    }

    // helper: desenha uma lista de socios sob um titulo (reusado por 4.1 e 5.1)
    const socioComConteudo = s =>
      s.nome || s.cpf || s.endereco || s.registro_criminal;
    const desenhaSocios = (titulo, lista) => {
      const preenchidos = (lista || []).filter(socioComConteudo);
      if (preenchidos.length === 0) return;
      // subitem .1: aplica recuo (indentacao) no titulo e nas linhas, como na
      // tela; restaura o recuo ao final.
      recuo = 6;
      tituloBloco(titulo);
      preenchidos.forEach((s, i) => {
        const tipo = s.tipo === 'PJ' ? ' (Sócio PJ)' : '';
        linhaCampo(`Sócio ${i + 1}${tipo}: `, s.nome || '—');
        linhaCampo(
          s.tipo === 'PJ' ? 'CNPJ: ' : 'CPF: ',
          s.cpf && formataDocumento(s.cpf, s.tipo)
        );
        linhaCampo('Endereço: ', s.endereco);
        linhaCampo('Registro Criminal: ', s.registro_criminal);
        y += 1;
      });
      recuo = 0;
      y += 1;
    };

    // ---- BLOCO 4.1 - SOCIOS (PJ Remetente). socios legados (sem secao) = 4.1
    const todosSocios = rt.relatorio_tecnico_socios || [];
    // 4.1 = socios com secao '4.1' OU legados sem secao (null/undefined)
    const ehSecao41 = s => !s.secao || s.secao === '4.1';
    desenhaSocios('4.1. SÓCIOS', todosSocios.filter(ehSecao41));

    // ---- BLOCO 5 + 5.1 - PJ EMITENTE DA NF-e: so quando ha divergencia ----
    if (rt.coincide_nfe === 'NAO') {
      const temEmitente =
        rt.emitente_razao_social ||
        rt.emitente_cnpj ||
        rt.emitente_data_abertura ||
        rt.emitente_capital_social ||
        rt.emitente_endereco;

      if (temEmitente) {
        tituloBloco('5. PJ EMITENTE DA NF-e');
        linhaCampo('Razão social: ', rt.emitente_razao_social);

        const partesEmit = [];
        if (rt.emitente_cnpj)
          partesEmit.push(`CNPJ: ${formataCNPJ(rt.emitente_cnpj)}`);
        if (rt.emitente_data_abertura)
          partesEmit.push(`Data abertura: ${fmtData(rt.emitente_data_abertura)}`);
        if (rt.emitente_capital_social)
          partesEmit.push(`Capital social: ${rt.emitente_capital_social}`);
        linhaTexto(partesEmit.join('    '));

        linhaCampo('Endereço: ', rt.emitente_endereco);
        y += 2;
      }

      desenhaSocios(
        '5.1. SÓCIOS',
        todosSocios.filter(s => s.secao === '5.1')
      );
    }

    // helper: desenha um bloco de PJ (Razao social, CNPJ/Data/Capital,
    // Endereco). Reutilizado por 6, 7 e 8. Retorna true se desenhou algo.
    const desenhaPJ = (titulo, dados) => {
      const tem =
        dados.razao ||
        dados.cnpj ||
        dados.dataAbertura ||
        dados.capital ||
        dados.endereco;
      if (!tem) return false;
      tituloBloco(titulo);
      linhaCampo('Razão social: ', dados.razao);
      const partes = [];
      if (dados.cnpj) partes.push(`CNPJ: ${formataCNPJ(dados.cnpj)}`);
      if (dados.dataAbertura)
        partes.push(`Data abertura: ${fmtData(dados.dataAbertura)}`);
      if (dados.capital) partes.push(`Capital social: ${dados.capital}`);
      linhaTexto(partes.join('    '));
      linhaCampo('Endereço: ', dados.endereco);
      y += 2;
      return true;
    };

    // ---- BLOCO 6 + 6.1 - PJ TRANSPORTADORA ----
    if (
      desenhaPJ('6. PJ TRANSPORTADORA', {
        razao: rt.transp_razao_social,
        cnpj: rt.transp_cnpj,
        dataAbertura: rt.transp_data_abertura,
        capital: rt.transp_capital_social,
        endereco: rt.transp_endereco,
      }) ||
      rt.coincide_cte
    ) {
      if (rt.coincide_cte) {
        const sim = rt.coincide_cte === 'SIM';
        linhaTexto(
          `Coincide com CT-e/MDF-e?  SIM ( ${sim ? 'X' : ' '} )   NÃO ( ${
            sim ? ' ' : 'X'
          } )`
        );
        y += 2;
      }
    }
    desenhaSocios('6.1. SÓCIOS', todosSocios.filter(s => s.secao === '6.1'));

    // ---- BLOCO 7 + 7.1 - PJ EMITENTE DO MANIFESTO: so quando divergencia ----
    if (rt.coincide_cte === 'NAO') {
      desenhaPJ('7. PJ EMITENTE DO MANIFESTO', {
        razao: rt.manifesto_razao_social,
        cnpj: rt.manifesto_cnpj,
        dataAbertura: rt.manifesto_data_abertura,
        capital: rt.manifesto_capital_social,
        endereco: rt.manifesto_endereco,
      });
      desenhaSocios(
        '7.1. SÓCIOS',
        todosSocios.filter(s => s.secao === '7.1')
      );
    }

    // ---- BLOCO 8 + 8.1 - PJ DESTINATARIO (sempre) ----
    desenhaPJ('8. PJ DESTINATÁRIO', {
      razao: rt.dest_razao_social,
      cnpj: rt.dest_cnpj,
      dataAbertura: rt.dest_data_abertura,
      capital: rt.dest_capital_social,
      endereco: rt.dest_endereco,
    });
    desenhaSocios('8.1. SÓCIOS', todosSocios.filter(s => s.secao === '8.1'));

    // ---- CAMPO 8 (DINAMICA): texto livre + IMAGENS coladas, com PAGINACAO ----
    // A dinamica agora pode conter HTML (texto + <img> base64). RTs antigos
    // guardam texto puro (sem tags) - tratados como um unico bloco de texto.
    if (rt.dinamica && String(rt.dinamica).trim() !== '') {
      const larguraTexto = margemDir - (margemEsq + 4) - 4;
      const alturaLinha = 5; // mm por linha
      const alturaMaxPag = conteudoBase - (molduraTopo + 8);

      const xEsq = margemEsq + 4;
      const xDir = margemDir - 4;
      const xCentro = (xEsq + xDir) / 2;

      // escreve um bloco de texto respeitando alinhamento (left/center/right/
      // justify) e negrito, com quebra de linha e paginacao.
      const escreveTexto = (txt, align, bold) => {
        if (!txt || !String(txt).trim()) return;
        const estilo = bold ? 'bold' : 'normal';
        doc.setFont('helvetica', estilo);
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        const linhas = doc.splitTextToSize(String(txt), larguraTexto);
        linhas.forEach(linha => {
          if (y + alturaLinha > conteudoBase) {
            y = novaPaginaDocumento();
            doc.setFont('helvetica', estilo);
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);
          }
          if (align === 'center') {
            doc.text(linha, xCentro, y, { align: 'center' });
          } else if (align === 'right') {
            doc.text(linha, xDir, y, { align: 'right' });
          } else if (align === 'justify') {
            doc.text(linha, xEsq, y, {
              align: 'justify',
              maxWidth: larguraTexto,
            });
          } else {
            doc.text(linha, xEsq, y);
          }
          y += alturaLinha;
        });
      };

      // desenha uma imagem base64 escalada, respeitando alinhamento (posicao
      // horizontal) e page-break; limita a altura a 1 pagina util.
      const escreveImagem = (src, align) => {
        try {
          const props = doc.getImageProperties(src);
          let larg = larguraTexto;
          let alt = (props.height / props.width) * larg;
          if (alt > alturaMaxPag) {
            alt = alturaMaxPag;
            larg = (props.width / props.height) * alt;
          }
          if (y + alt > conteudoBase) {
            y = novaPaginaDocumento();
          }
          let x = xEsq;
          if (align === 'center') x = xCentro - larg / 2;
          else if (align === 'right') x = xDir - larg;
          const fmt = (props.fileType || 'PNG').toUpperCase();
          doc.addImage(src, fmt, x, y, larg, alt);
          y += alt + 3;
        } catch (e) {
          // imagem invalida: ignora, nao quebra a geracao do PDF
        }
      };

      // extrai o alinhamento de um elemento (classe ql-align-* ou style)
      const leAlign = el => {
        const cls = el.getAttribute && el.getAttribute('class');
        if (cls) {
          if (cls.indexOf('ql-align-center') >= 0) return 'center';
          if (cls.indexOf('ql-align-right') >= 0) return 'right';
          if (cls.indexOf('ql-align-justify') >= 0) return 'justify';
        }
        const st = el.getAttribute && el.getAttribute('style');
        if (st) {
          const m = st.match(/text-align:\s*(center|right|justify)/i);
          if (m) return m[1].toLowerCase();
        }
        return null;
      };

      const html = String(rt.dinamica);
      const temHtml = /<[a-z!/][\s\S]*>/i.test(html);

      if (!temHtml) {
        // RT antigo: texto puro
        escreveTexto(html, 'left', false);
      } else {
        // percorre os nos do HTML na ordem. alignHerdado desce dos blocos pais;
        // boldHerdado marca <strong>/<b> para o texto interno.
        const doc2 = new DOMParser().parseFromString(html, 'text/html');
        const percorre = (no, alignHerdado, boldHerdado) => {
          no.childNodes.forEach(filho => {
            if (filho.nodeType === 3) {
              escreveTexto(filho.textContent, alignHerdado, boldHerdado);
            } else if (filho.nodeType === 1) {
              const tag = filho.tagName.toLowerCase();
              if (tag === 'img') {
                const src = filho.getAttribute('src');
                if (src && src.indexOf('data:image') === 0)
                  escreveImagem(src, alignHerdado);
              } else if (tag === 'br') {
                y += alturaLinha;
              } else {
                const align = leAlign(filho) || alignHerdado;
                const bold =
                  boldHerdado || tag === 'strong' || tag === 'b';
                const ehBloco = /^(p|div|li|ul|ol|h1|h2|h3|blockquote|pre)$/.test(
                  tag
                );
                percorre(filho, align, bold);
                if (ehBloco) y += 1; // espaco apos bloco
              }
            }
          });
        };
        percorre(doc2.body, 'left', false);
      }
    }

    const anexos = rt.relatorio_tecnico_pdfs || [];
    // sem anexos: retorna so o documento do RT (Blob)
    if (anexos.length === 0) {
      return doc.output('blob');
    }

    // COM anexos: mescla o documento do RT + cada anexo num PDF unico (pdf-lib)
    try {
      const rtBytes = doc.output('arraybuffer');
      const merged = await PDFDocument.load(rtBytes);

      // fonte + texto do carimbo (marca dagua no topo de cada pagina anexa)
      const fonteCarimbo = await merged.embedFont(StandardFonts.HelveticaBold);
      const textoCarimbo = `DOCUMENTO ANEXO - ${prefixoDoDocumento(rt)} nº ${String(
        rt.numero
      ).padStart(4, '0')}/${rt.ano}`;

      // desenha uma faixa cinza translucida + texto no topo da pagina
      const carimbaPagina = pg => {
        const { width, height } = pg.getSize();
        const alturaFaixa = 26;
        pg.drawRectangle({
          x: 0,
          y: height - alturaFaixa,
          width,
          height: alturaFaixa,
          color: rgb(0.5, 0.5, 0.5),
          opacity: 0.35,
        });
        pg.drawText(textoCarimbo, {
          x: 14,
          y: height - 17,
          size: 9,
          font: fonteCarimbo,
          color: rgb(0.15, 0.15, 0.15),
          opacity: 0.85,
        });
      };

      for (const anexo of anexos) {
        try {
          const resp = await api.get('/rt-pdf-download', {
            params: { id: anexo.id },
            responseType: 'arraybuffer',
          });
          const bytes = resp.data;
          const nome = (anexo.nome || '').toLowerCase();

          if (nome.endsWith('.pdf')) {
            // anexo PDF -> copia as paginas e carimba cada uma
            const anexoDoc = await PDFDocument.load(bytes);
            const paginas = await merged.copyPages(
              anexoDoc,
              anexoDoc.getPageIndices()
            );
            paginas.forEach(pg => {
              merged.addPage(pg);
              carimbaPagina(pg);
            });
          } else if (nome.endsWith('.png')) {
            const img = await merged.embedPng(bytes);
            const pg = merged.addPage();
            const { width, height } = pg.getSize();
            const dim = img.scaleToFit(width - 40, height - 40);
            pg.drawImage(img, {
              x: (width - dim.width) / 2,
              y: (height - dim.height) / 2,
              width: dim.width,
              height: dim.height,
            });
            carimbaPagina(pg);
          } else if (nome.endsWith('.jpg') || nome.endsWith('.jpeg')) {
            const img = await merged.embedJpg(bytes);
            const pg = merged.addPage();
            const { width, height } = pg.getSize();
            const dim = img.scaleToFit(width - 40, height - 40);
            pg.drawImage(img, {
              x: (width - dim.width) / 2,
              y: (height - dim.height) / 2,
              width: dim.width,
              height: dim.height,
            });
            carimbaPagina(pg);
          } else {
            // tipo nao mesclavel (ex.: docx, xlsx): adiciona uma PAGINA DE
            // AVISO no PDF e baixa o arquivo separadamente.
            const pg = merged.addPage();
            const { width, height } = pg.getSize();
            const fonte = await merged.embedFont(StandardFonts.Helvetica);
            const fonteBold = await merged.embedFont(
              StandardFonts.HelveticaBold
            );
            pg.drawText('ANEXO', {
              x: 50,
              y: height - 80,
              size: 16,
              font: fonteBold,
            });
            pg.drawText(anexo.nome, {
              x: 50,
              y: height - 110,
              size: 12,
              font: fonte,
            });
            pg.drawText(
              'Este anexo nao pode ser incorporado ao PDF.',
              { x: 50, y: height - 135, size: 11, font: fonte }
            );
            pg.drawText(
              'O arquivo foi disponibilizado para download separado.',
              { x: 50, y: height - 152, size: 11, font: fonte }
            );
            // baixa o arquivo original separadamente (so no fluxo Imprimir)
            if (baixarNaoMesclaveis) {
              const blob = new Blob([bytes]);
              saveAs(blob, anexo.nome);
            }
          }
        } catch (e) {
          // se um anexo falhar, segue com os demais
        }
      }

      const mergedBytes = await merged.save();
      return new Blob([mergedBytes], { type: 'application/pdf' });
    } catch (e) {
      // fallback: se a mesclagem falhar, retorna ao menos o documento do RT
      toast.error('Falha ao anexar arquivos; documento gerado sem anexos.');
      return doc.output('blob');
    }
  };

  const nomeArqRT = rt =>
    `${prefixoArqTipo}_${String(rt.numero).padStart(4, '0')}_${rt.ano}.pdf`;

  // "Imprimir": gera o PDF e baixa (fluxo antigo, mantido).
  const imprimirRT = async rt => {
    const blob = await gerarPdfRT(rt, { baixarNaoMesclaveis: true });
    if (blob) saveAs(blob, nomeArqRT(rt));
  };

  // "Ver": gera o PDF e exibe no layout impresso dentro de uma modal grande.
  const verRT = async rt => {
    setRtPreview(rt);
    setGerandoPdf(true);
    setPdfUrl('');
    try {
      const blob = await gerarPdfRT(rt, { baixarNaoMesclaveis: false });
      if (blob) setPdfUrl(URL.createObjectURL(blob));
    } catch (e) {
      toast.error('Falha ao gerar a visualização do documento');
    } finally {
      setGerandoPdf(false);
    }
  };

  // fecha a preview e libera a URL do blob (evita vazamento de memoria)
  const fecharPreview = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl('');
    setRtPreview(null);
  };

  return (
    <NavBar>
      <MDBContainer fluid className="my-3">
        <MDBRow className="h4-responsive" center>
          {tituloExibicao}
        </MDBRow>
        <MDBRow center className="mb-2">
          {podeCriar ? (
            <Link to={rotaCriar}>
              <MDBBtn color="mdb-color" size="sm">
                <MDBIcon icon="plus" className="mr-2" />
                {textoNovo}
              </MDBBtn>
            </Link>
          ) : (
            // sem acesso a criacao: botao desabilitado (sem Link, que
            // navegaria mesmo com o botao disabled)
            <MDBBtn
              color="mdb-color"
              size="sm"
              disabled
              title={`Você não possui acesso para criar ${nomeDoc}`}
            >
              <MDBIcon icon="plus" className="mr-2" />
              {textoNovo}
            </MDBBtn>
          )}
        </MDBRow>

        <MDBRow center className="mb-3">
          <span className="align-self-center mr-2 grey-text">
            {exportando ? 'Exportando…' : 'Exportar:'}
          </span>
          <MDBBtn color="success" size="sm" disabled={exportando} onClick={exportarXLSX}>
            <MDBIcon icon="file-excel" className="mr-1" />
            XLSX
          </MDBBtn>
          <MDBBtn color="info" size="sm" disabled={exportando} onClick={exportarCSV}>
            <MDBIcon icon="file-excel-o" className="mr-1" />
            CSV
          </MDBBtn>
          <MDBBtn color="danger" size="sm" disabled={exportando} onClick={exportarPDF}>
            <MDBIcon icon="file-pdf" className="mr-1" />
            PDF
          </MDBBtn>
          <MDBBtn color="primary" size="sm" disabled={exportando} onClick={exportarI2}>
            <MDBIcon icon="sitemap" className="mr-1" />
            i2
          </MDBBtn>
        </MDBRow>

        {/* filtros */}
        <MDBCard className="mb-3">
          <MDBCardBody>
            {/* busca livre: procura em qualquer parte do documento */}
            <MDBRow>
              <MDBCol size="12">
                <span style={{ fontSize: '13px' }}>
                  Buscar no documento (assunto, dinâmica, sócios, PJs, nº...)
                </span>
                <MDBInput
                  type="text"
                  hint="Digite qualquer termo e clique em Buscar"
                  value={busca}
                  onChange={e => setBusca(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      setPage(1);
                      buscar();
                    }
                  }}
                />
              </MDBCol>
            </MDBRow>
            <MDBRow className="align-items-center">
              <MDBCol size="12" md="4">
                <span style={{ fontSize: '13px' }}>Data Início</span>
                <MDBInput
                  type="date"
                  value={dataInicio}
                  onChange={e => setDataInicio(e.target.value)}
                />
              </MDBCol>
              <MDBCol size="12" md="4">
                <span style={{ fontSize: '13px' }}>Data Fim</span>
                <MDBInput
                  type="date"
                  value={dataFinal}
                  onChange={e => setDataFinal(e.target.value)}
                />
              </MDBCol>
              <MDBCol size="12" md="4" className="text-center">
                <MDBBtn
                  size="sm"
                  onClick={() => {
                    setPage(1);
                    buscar();
                  }}
                >
                  <MDBIcon icon="search" className="mr-2" />
                  Buscar
                </MDBBtn>
              </MDBCol>
            </MDBRow>
          </MDBCardBody>
        </MDBCard>

        {/* cabecalho da tabela */}
        <MDBRow
          style={{ borderRadius: '5px' }}
          className="p-2 mr-1 ml-1 align-items-center grey darken-1 d-none d-md-flex text-white font-weight-bolder"
        >
          <MDBCol size="12" md="2">
            Nº / Ano
          </MDBCol>
          <MDBCol size="12" md="2">
            Data
          </MDBCol>
          <MDBCol size="12" md="3">
            Assunto
          </MDBCol>
          <MDBCol size="12" md="2">
            Autor
          </MDBCol>
          <MDBCol size="12" md="3" />
        </MDBRow>

        {loading ? (
          <MDBRow center className="my-4 align-items-center">
            <MDBSpinner yellow />
            <span className="ml-3 font-weight-bolder">Carregando...</span>
          </MDBRow>
        ) : relatorios.length === 0 ? (
          <MDBRow center className="my-4 font-weight-bolder">
            {`Nenhum${docFeminino ? 'a' : ''} ${nomeDoc} encontrad${
              docFeminino ? 'a' : 'o'
            }`}
          </MDBRow>
        ) : (
          relatorios.map(rt => (
            <MDBRow
              key={rt.id}
              style={{ borderRadius: '5px' }}
              className="p-2 mr-1 ml-1 mt-1 align-items-center bg-white border-top"
            >
              <MDBCol size="12" md="2" className="font-weight-bolder">
                <span className="d-sm-inline d-md-none mr-2">Nº/Ano:</span>
                {formataNumero(rt)}
              </MDBCol>
              <MDBCol size="12" md="2">
                <span className="d-sm-inline d-md-none mr-2">Data:</span>
                {rt.data ? format(parseISO(rt.data), 'dd/MM/yyyy') : '—'}
              </MDBCol>
              <MDBCol size="12" md="3">
                <span className="d-sm-inline d-md-none mr-2">Assunto:</span>
                {rt.assunto || '—'}
              </MDBCol>
              <MDBCol size="12" md="2">
                <span className="d-sm-inline d-md-none mr-2">Autor:</span>
                {nomeAutor(rt)}
              </MDBCol>
              <MDBCol
                size="12"
                md="3"
                className="d-flex justify-content-center justify-content-md-end"
              >
                <span
                  className="mr-3 font-weight-bold"
                  style={{ cursor: 'pointer', color: '#59698d' }}
                  onClick={() => verRT(rt)}
                >
                  <MDBIcon far icon="eye" className="mr-1" />
                  Ver
                </span>
                <span
                  className="mr-3 font-weight-bold"
                  style={{ cursor: 'pointer', color: '#2e7d32' }}
                  onClick={() => imprimirRT(rt)}
                >
                  <MDBIcon icon="print" className="mr-1" />
                  Imprimir
                </span>
                <span
                  className="danger-text font-weight-bold"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setIdExcluir(rt.id)}
                >
                  <MDBIcon far icon="trash-alt" className="mr-1" />
                  Excluir
                </span>
              </MDBCol>
            </MDBRow>
          ))
        )}

        {/* paginacao */}
        <MDBRow center className="my-4 align-items-center">
          <div>
            Página {page} de {qtdTotal}
          </div>
          <MDBIcon
            className="mx-3"
            style={{ fontSize: '28px', cursor: 'pointer' }}
            icon="angle-left"
            onClick={() => {
              if (page > 1) setPage(page - 1);
            }}
          />
          |
          <MDBIcon
            className="mx-3"
            style={{ fontSize: '28px', cursor: 'pointer' }}
            icon="angle-right"
            onClick={() => {
              if (page < qtdTotal) setPage(page + 1);
            }}
          />
        </MDBRow>
      </MDBContainer>

      {/* MODAL DE DETALHES */}
      {/* MODAL DE VISUALIZACAO DO DOCUMENTO (layout impresso, via PDF) */}
      <MDBModal size="lg" isOpen={rtPreview !== null} toggle={fecharPreview}>
        <MDBModalHeader toggle={fecharPreview}>
          {rtPreview
            ? `${ehRelint ? 'RELINT' : 'RT'} nº ${formataNumero(rtPreview)}`
            : ''}
        </MDBModalHeader>
        <MDBModalBody style={{ padding: 0 }}>
          {gerandoPdf && (
            <div className="text-center p-5">
              <MDBSpinner />
              <p className="mt-3 mb-0">Gerando o documento...</p>
            </div>
          )}
          {!gerandoPdf && pdfUrl && (
            <iframe
              title="Documento do Relatório Técnico"
              src={pdfUrl}
              style={{
                width: '100%',
                height: '80vh',
                border: 'none',
              }}
            />
          )}
          {!gerandoPdf && !pdfUrl && (
            <div className="text-center p-5 text-muted">
              Não foi possível gerar a visualização.
            </div>
          )}
        </MDBModalBody>
        <MDBModalFooter between>
          <MDBBtn color="danger" onClick={fecharPreview}>
            Fechar
          </MDBBtn>
          <div>
            {pdfUrl && (
              <MDBBtn
                color="info"
                onClick={() => saveAs(pdfUrl, nomeArqRT(rtPreview))}
              >
                <MDBIcon icon="download" className="mr-2" />
                Baixar
              </MDBBtn>
            )}
            <MDBBtn color="success" onClick={() => imprimirRT(rtPreview)}>
              <MDBIcon icon="print" className="mr-2" />
              Imprimir
            </MDBBtn>
          </div>
        </MDBModalFooter>
      </MDBModal>

      {/* modal antiga de campos (desativada - substituida pela preview do
          documento). Mantida vazia p/ nao renderizar. */}
      {false && (
      <MDBModal
        size="lg"
        isOpen={detalhe !== null}
        toggle={() => setDetalhe(null)}
      >
        <MDBModalHeader toggle={() => setDetalhe(null)}>
          {detalhe ? `RT nº ${formataNumero(detalhe)}` : ''}
        </MDBModalHeader>
        <MDBModalBody>
          {detalhe && (
            <>
              <p>
                <b>Precedência:</b> {detalhe.precedencia || '—'}
              </p>
              <p>
                <b>1. Data:</b>{' '}
                {detalhe.data
                  ? format(parseISO(detalhe.data), 'dd/MM/yyyy')
                  : '—'}
              </p>
              <p>
                <b>2. Assunto:</b> {detalhe.assunto || '—'}
              </p>
              <p>
                <b>3. Origem:</b> {detalhe.origem || '—'}
              </p>
              {ehRelint && (
                <p>
                  <b>4. Avaliação:</b> {detalhe.avaliacao || '—'}
                </p>
              )}
              <p>
                <b>{ehRelint ? '5. Difusão:' : '4. Difusão:'}</b>{' '}
                {detalhe.difusao || '—'}
              </p>
              <p>
                <b>
                  {ehRelint
                    ? '6. Difusão Anterior:'
                    : '5. Difusão Anterior:'}
                </b>{' '}
                {detalhe.difusao_anterior || '—'}
              </p>
              <p>
                <b>{ehRelint ? '7. Referência:' : '6. Referência:'}</b>{' '}
                {detalhe.referencia || '—'}
              </p>
              <p>
                <b>PCF (Posto de Controle Fiscal):</b> {detalhe.pcf || '—'}
              </p>

              {/* BLOCO MOTORISTA */}
              {(detalhe.motorista_nome ||
                detalhe.motorista_cpf ||
                detalhe.motorista_cnh ||
                detalhe.motorista_cat ||
                detalhe.motorista_validade ||
                detalhe.motorista_endereco ||
                detalhe.motorista_registro_criminal) && (
                <div className="mb-2">
                  <p className="font-weight-bold mb-1">MOTORISTA</p>
                  <p className="mb-0">Nome: {detalhe.motorista_nome || '—'}</p>
                  <p className="mb-0">
                    CPF:{' '}
                    {detalhe.motorista_cpf
                      ? formataCPF(detalhe.motorista_cpf)
                      : '—'}
                  </p>
                  <p className="mb-0">
                    CNH:{' '}
                    {detalhe.motorista_cnh
                      ? formataCNH(detalhe.motorista_cnh)
                      : '—'}{' '}
                    &nbsp; Cat.: {detalhe.motorista_cat || '—'} &nbsp; Validade:{' '}
                    {detalhe.motorista_validade
                      ? fmtData(detalhe.motorista_validade)
                      : '—'}
                  </p>
                  <p className="mb-0">
                    Endereço: {detalhe.motorista_endereco || '—'}
                  </p>
                  <p className="mb-0">
                    Registro Criminal:{' '}
                    {detalhe.motorista_registro_criminal || '—'}
                  </p>
                </div>
              )}

              {/* BLOCO VEICULO (CAVALO MECANICO) */}
              {(detalhe.veiculo_marca_modelo ||
                detalhe.veiculo_tipo ||
                detalhe.veiculo_placa ||
                detalhe.veiculo_cor ||
                detalhe.veiculo_ano ||
                detalhe.veiculo_chassi ||
                detalhe.veiculo_proprietario ||
                detalhe.veiculo_cpf_cnpj) && (
                <div className="mb-2">
                  <p className="font-weight-bold mb-1">
                    2. VEÍCULO (CAVALO MECÂNICO)
                  </p>
                  <p className="mb-0">
                    Marca/Modelo: {detalhe.veiculo_marca_modelo || '—'}
                  </p>
                  <p className="mb-0">
                    Tipo: {detalhe.veiculo_tipo || '—'} &nbsp; Placa:{' '}
                    {detalhe.veiculo_placa || '—'} &nbsp; Cor:{' '}
                    {detalhe.veiculo_cor || '—'}
                  </p>
                  <p className="mb-0">
                    Ano: {detalhe.veiculo_ano || '—'} &nbsp; Chassi:{' '}
                    {detalhe.veiculo_chassi || '—'}
                  </p>
                  <p className="mb-0">
                    Proprietário: {detalhe.veiculo_proprietario || '—'}
                  </p>
                  <p className="mb-0">
                    CPF/CNPJ:{' '}
                    {detalhe.veiculo_cpf_cnpj
                      ? formataCpfCnpj(detalhe.veiculo_cpf_cnpj)
                      : '—'}
                  </p>
                </div>
              )}

              {/* BLOCO VEICULO (SEMIRREBOQUE) */}
              {(detalhe.semirreboque_placa ||
                detalhe.semirreboque_chassi ||
                detalhe.semirreboque_proprietario ||
                detalhe.semirreboque_cpf_cnpj) && (
                <div className="mb-2">
                  <p className="font-weight-bold mb-1">
                    3. VEÍCULO (SEMIRREBOQUE)
                  </p>
                  <p className="mb-0">
                    Placa: {detalhe.semirreboque_placa || '—'} &nbsp; Chassi:{' '}
                    {detalhe.semirreboque_chassi || '—'}
                  </p>
                  <p className="mb-0">
                    Proprietário: {detalhe.semirreboque_proprietario || '—'}
                  </p>
                  <p className="mb-0">
                    CPF/CNPJ:{' '}
                    {detalhe.semirreboque_cpf_cnpj
                      ? formataCpfCnpj(detalhe.semirreboque_cpf_cnpj)
                      : '—'}
                  </p>
                </div>
              )}

              {/* BLOCO 4 - PJ REMETENTE DA CARGA */}
              {(detalhe.pj_razao_social ||
                detalhe.pj_cnpj ||
                detalhe.pj_data_abertura ||
                detalhe.pj_capital_social ||
                detalhe.pj_endereco ||
                detalhe.coincide_nfe) && (
                <div className="mb-2">
                  <p className="font-weight-bold mb-1">
                    4. PJ REMETENTE DA CARGA
                  </p>
                  <p className="mb-0">
                    Razão social: {detalhe.pj_razao_social || '—'}
                  </p>
                  <p className="mb-0">
                    CNPJ: {detalhe.pj_cnpj ? formataCNPJ(detalhe.pj_cnpj) : '—'}{' '}
                    &nbsp; Data abertura:{' '}
                    {detalhe.pj_data_abertura
                      ? fmtData(detalhe.pj_data_abertura)
                      : '—'}{' '}
                    &nbsp; Capital social:{' '}
                    {detalhe.pj_capital_social || '—'}
                  </p>
                  <p className="mb-0">
                    Endereço: {detalhe.pj_endereco || '—'}
                  </p>
                  <p className="mb-0">
                    Coincide com a NF-e?{' '}
                    {detalhe.coincide_nfe === 'SIM'
                      ? 'SIM'
                      : detalhe.coincide_nfe === 'NAO'
                      ? 'NÃO'
                      : '—'}
                  </p>
                </div>
              )}

              {/* BLOCO 4.1 - SOCIOS (PJ Remetente). legados sem secao = 4.1 */}
              <DetalheSocios
                titulo="4.1. SÓCIOS"
                socios={(detalhe.relatorio_tecnico_socios || []).filter(
                  s => !s.secao || s.secao === '4.1'
                )}
              />

              {/* BLOCO 5 - PJ EMITENTE DA NF-e: so quando ha divergencia */}
              {detalhe.coincide_nfe === 'NAO' &&
                (detalhe.emitente_razao_social ||
                  detalhe.emitente_cnpj ||
                  detalhe.emitente_data_abertura ||
                  detalhe.emitente_capital_social ||
                  detalhe.emitente_endereco) && (
                  <div className="mb-2">
                    <p className="font-weight-bold mb-1">
                      5. PJ EMITENTE DA NF-e
                    </p>
                    <p className="mb-0">
                      Razão social: {detalhe.emitente_razao_social || '—'}
                    </p>
                    <p className="mb-0">
                      CNPJ:{' '}
                      {detalhe.emitente_cnpj
                        ? formataCNPJ(detalhe.emitente_cnpj)
                        : '—'}{' '}
                      &nbsp; Data abertura:{' '}
                      {detalhe.emitente_data_abertura
                        ? fmtData(detalhe.emitente_data_abertura)
                        : '—'}{' '}
                      &nbsp; Capital social:{' '}
                      {detalhe.emitente_capital_social || '—'}
                    </p>
                    <p className="mb-0">
                      Endereço: {detalhe.emitente_endereco || '—'}
                    </p>
                  </div>
                )}

              {/* BLOCO 5.1 - SOCIOS (PJ Emitente): so quando ha divergencia */}
              {detalhe.coincide_nfe === 'NAO' &&
                (detalhe.relatorio_tecnico_socios || []).filter(
                  s => s.secao === '5.1'
                ).length > 0 && (
                  <div className="mb-2">
                    <p className="font-weight-bold mb-1">5.1. SÓCIOS</p>
                    {(detalhe.relatorio_tecnico_socios || [])
                      .filter(s => s.secao === '5.1')
                      .map((s, i) => (
                        <div key={s.id} className="mb-1">
                          <p className="mb-0 font-weight-bold">
                            Sócio {i + 1}
                            {s.tipo === 'PJ' ? ' (Sócio PJ)' : ''}
                          </p>
                          <p className="mb-0">
                            {s.tipo === 'PJ' ? 'CNPJ' : 'CPF'}: {s.cpf || '—'}{' '}
                            &nbsp; Nome: {s.nome || '—'}
                          </p>
                          <p className="mb-0">Endereço: {s.endereco || '—'}</p>
                          <p className="mb-0">
                            Registro Criminal: {s.registro_criminal || '—'}
                          </p>
                        </div>
                      ))}
                  </div>
                )}

              {/* BLOCO 6 + 6.1 - PJ TRANSPORTADORA */}
              <DetalhePJ
                titulo="6. PJ TRANSPORTADORA"
                razao={detalhe.transp_razao_social}
                cnpj={detalhe.transp_cnpj}
                dataAbertura={detalhe.transp_data_abertura}
                capital={detalhe.transp_capital_social}
                endereco={detalhe.transp_endereco}
              />
              {detalhe.coincide_cte && (
                <p className="mb-2">
                  Coincide com CT-e/MDF-e?{' '}
                  {detalhe.coincide_cte === 'SIM'
                    ? 'SIM'
                    : detalhe.coincide_cte === 'NAO'
                    ? 'NÃO'
                    : '—'}
                </p>
              )}
              <DetalheSocios
                titulo="6.1. SÓCIOS"
                socios={(detalhe.relatorio_tecnico_socios || []).filter(
                  s => s.secao === '6.1'
                )}
              />

              {/* BLOCO 7 + 7.1 - PJ EMITENTE DO MANIFESTO: so quando divergencia */}
              {detalhe.coincide_cte === 'NAO' && (
                <>
                  <DetalhePJ
                    titulo="7. PJ EMITENTE DO MANIFESTO"
                    razao={detalhe.manifesto_razao_social}
                    cnpj={detalhe.manifesto_cnpj}
                    dataAbertura={detalhe.manifesto_data_abertura}
                    capital={detalhe.manifesto_capital_social}
                    endereco={detalhe.manifesto_endereco}
                  />
                  <DetalheSocios
                    titulo="7.1. SÓCIOS"
                    socios={(detalhe.relatorio_tecnico_socios || []).filter(
                      s => s.secao === '7.1'
                    )}
                  />
                </>
              )}

              {/* BLOCO 8 + 8.1 - PJ DESTINATARIO (sempre) */}
              <DetalhePJ
                titulo="8. PJ DESTINATÁRIO"
                razao={detalhe.dest_razao_social}
                cnpj={detalhe.dest_cnpj}
                dataAbertura={detalhe.dest_data_abertura}
                capital={detalhe.dest_capital_social}
                endereco={detalhe.dest_endereco}
              />
              <DetalheSocios
                titulo="8.1. SÓCIOS"
                socios={(detalhe.relatorio_tecnico_socios || []).filter(
                  s => s.secao === '8.1'
                )}
              />

              <div className="mb-2" style={{ overflowWrap: 'anywhere' }}>
                {!ehSemDinamica && (
                  <>
                    <b>{ehRelint ? '9. Dinâmica:' : '8. Dinâmica:'}</b>{' '}
                    <DinamicaView html={detalhe.dinamica} />
                  </>
                )}
                {ehSemDinamica && (
                  <>
                    <div><b>1. Dados Conhecidos:</b> {detalhe.dados_conhecidos || ''}</div>
                    <div><b>2. Dados Solicitados:</b> {detalhe.dados_solicitados || ''}</div>
                    <div><b>3. Instruções Especiais:</b> {detalhe.instrucoes_especiais || ''}</div>
                  </>
                )}
              </div>
              <hr />
              <p className="font-weight-bold">
                {ehRelint ? '8. Anexos:' : '7. Anexos:'}
              </p>
              {detalhe.relatorio_tecnico_pdfs &&
              detalhe.relatorio_tecnico_pdfs.length > 0 ? (
                detalhe.relatorio_tecnico_pdfs.map(anexo => (
                  <MDBRow
                    key={anexo.id}
                    className="align-items-center bg-light p-2 mx-1 my-1 rounded"
                  >
                    <MDBCol>
                      <MDBIcon
                        far
                        icon="file-pdf"
                        className="mr-2 text-danger"
                      />
                      {anexo.descricao || anexo.nome}
                      {anexo.descricao ? (
                        <span
                          className="grey-text ml-2"
                          style={{ fontSize: '11px' }}
                        >
                          ({anexo.nome})
                        </span>
                      ) : null}
                    </MDBCol>
                    <MDBCol size="12" md="3" className="text-md-right">
                      <span
                        className="blue-grey-text font-weight-bold"
                        style={{ cursor: 'pointer' }}
                        onClick={() => baixarAnexo(anexo.id, anexo.nome)}
                      >
                        <MDBIcon icon="download" className="mr-1" />
                        Baixar
                      </span>
                    </MDBCol>
                  </MDBRow>
                ))
              ) : (
                <span className="grey-text">Sem anexos</span>
              )}
            </>
          )}
        </MDBModalBody>
        <MDBModalFooter between>
          <MDBBtn color="danger" onClick={() => setDetalhe(null)}>
            Fechar
          </MDBBtn>
          <MDBBtn color="success" onClick={() => imprimirRT(detalhe)}>
            <MDBIcon icon="print" className="mr-2" />
            Imprimir
          </MDBBtn>
        </MDBModalFooter>
      </MDBModal>
      )}

      {/* MODAL DE EXCLUSAO */}
      <MDBModal isOpen={idExcluir !== null} toggle={() => setIdExcluir(null)}>
        <MDBModalHeader toggle={() => setIdExcluir(null)}>
          {`Excluir ${nomeDoc}`}
        </MDBModalHeader>
        <MDBModalBody className="text-danger font-weight-bolder">
          Tem certeza que deseja excluir este Relatório Técnico?
        </MDBModalBody>
        <MDBModalFooter between>
          <MDBBtn color="danger" onClick={() => setIdExcluir(null)}>
            Cancelar
          </MDBBtn>
          <MDBBtn color="warning" disabled={excluindo} onClick={excluir}>
            {excluindo ? 'Excluindo...' : 'Excluir'}
          </MDBBtn>
        </MDBModalFooter>
      </MDBModal>
    </NavBar>
  );
}
